/**
 * features/control/mock.ts — deterministic Kontrollbereich fixtures (mock data-mode).
 *
 * Reproduces Stefan's prototype exactly (V4.0-inbox/cockpit-screenshots): 10 open
 * elements across the 4 stages (3/3/2/2), the same absenders, betreffs, police/kunde
 * links, ages and signals. "Today" in the prototype is 26.08.2026 (dashboard "Stand"),
 * so `alter_tage` is hardcoded and `eingegangen_am` / `fristenstufe_seit` line up with
 * the detail screenshots. Betreff em dashes are rendered as hyphens (repo rule: no em
 * dashes) — the ONLY deviation from the screenshot glyphs.
 *
 * `alter_tage` is a server-computed field in the real contract, so the mock hardcodes
 * it (never derived from the real clock) — the render + tests stay stable over time.
 */

import type {
  KontrolleFristenDto,
  KontrolleRueckstandDetailDto,
  KontrolleUebersichtDto,
} from './types';
import { STUFE_NAME, STUFE_DEFAULT_TAGE } from './constants';

/** The 10 elements, in the screenshot order (per stage, oldest first). */
export const MOCK_RUECKSTAND: KontrolleRueckstandDetailDto[] = [
  // ── Stufe 1 · Beobachtung (≥2 Tage) ────────────────────────────────────────
  {
    id: 'kb000001-0000-0000-0000-000000000001',
    kanal: 'postscan',
    absender: 'Gebäudeversicherung AG',
    betreff: 'Prämienrechnung 2026',
    kunde_name: 'Fähndrich Immobilien AG',
    police_bezeichnung: 'GEB-2022-045',
    subject_type: 'firma',
    subject_id: 'ff000001-0000-0000-0000-000000000001',
    eingegangen_am: '2026-08-22T08:12:00.000Z',
    alter_tage: 4,
    stufe: 1,
    signale: [],
    verknuepft_mit: 'GEB-2022-045',
    fristenstufe_seit: '2026-08-22T08:12:00.000Z',
  },
  {
    id: 'kb000002-0000-0000-0000-000000000002',
    kanal: 'email',
    absender: 'Müller Transport AG',
    betreff: 'Nachtrag Flottenversicherung',
    kunde_name: 'Müller Transport AG',
    police_bezeichnung: 'MFZ-2024-118',
    subject_type: 'firma',
    subject_id: 'ff000002-0000-0000-0000-000000000002',
    eingegangen_am: '2026-08-23T10:30:00.000Z',
    alter_tage: 3,
    stufe: 1,
    signale: [],
    verknuepft_mit: 'MFZ-2024-118',
    fristenstufe_seit: '2026-08-23T10:30:00.000Z',
  },
  {
    id: 'kb000003-0000-0000-0000-000000000003',
    kanal: 'portal',
    absender: 'S. Meier (Fähndrich HR)',
    betreff: 'Frage zu BVG-Anschluss',
    kunde_name: 'Fähndrich Finanz AG',
    police_bezeichnung: null,
    subject_type: 'firma',
    subject_id: 'ff000003-0000-0000-0000-000000000003',
    eingegangen_am: '2026-08-23T14:05:00.000Z',
    alter_tage: 3,
    stufe: 1,
    signale: [],
    verknuepft_mit: 'Fähndrich Finanz AG',
    fristenstufe_seit: '2026-08-23T14:05:00.000Z',
  },

  // ── Stufe 2 · Nachfassen (≥5 Tage) ─────────────────────────────────────────
  {
    id: 'kb000004-0000-0000-0000-000000000004',
    kanal: 'portal',
    absender: 'P. Huber (Fuhrpark)',
    betreff: 'Neues Fahrzeug - Versicherungswunsch',
    kunde_name: 'Huber Logistik AG',
    police_bezeichnung: null,
    subject_type: 'firma',
    subject_id: 'ff000004-0000-0000-0000-000000000004',
    eingegangen_am: '2026-08-18T09:00:00.000Z',
    alter_tage: 8,
    stufe: 2,
    signale: [],
    verknuepft_mit: 'Huber Logistik AG',
    fristenstufe_seit: '2026-08-18T09:00:00.000Z',
  },
  {
    id: 'kb000005-0000-0000-0000-000000000005',
    kanal: 'versicherer_api',
    absender: 'Zurich Schaden-Center',
    betreff: 'Schadenfall SC-8834 - Rückfrage',
    kunde_name: 'Rbinaldi Transport AG',
    police_bezeichnung: 'MFZ-2023-071',
    subject_type: 'firma',
    subject_id: 'ff000005-0000-0000-0000-000000000005',
    eingegangen_am: '2026-08-19T11:20:00.000Z',
    alter_tage: 7,
    stufe: 2,
    signale: [{ typ: 'schadenbezug', quelle: 'regelbasiert' }],
    verknuepft_mit: 'MFZ-2023-071',
    fristenstufe_seit: '2026-08-19T11:20:00.000Z',
  },
  {
    id: 'kb000006-0000-0000-0000-000000000006',
    kanal: 'email',
    absender: 'Kanzlei Herzog & Partner',
    betreff: 'Mahnung - 2. Erinnerung Zahlungsverzug',
    kunde_name: 'Rossi Bau GmbH',
    police_bezeichnung: null,
    subject_type: 'firma',
    subject_id: 'ff000006-0000-0000-0000-000000000006',
    eingegangen_am: '2026-08-20T07:45:00.000Z',
    alter_tage: 6,
    stufe: 2,
    signale: [{ typ: 'wiederholter_kontakt', quelle: 'regelbasiert' }],
    verknuepft_mit: 'Rossi Bau GmbH',
    fristenstufe_seit: '2026-08-20T07:45:00.000Z',
  },

  // ── Stufe 3 · Kritisch (≥10 Tage) ──────────────────────────────────────────
  {
    id: 'kb000007-0000-0000-0000-000000000007',
    kanal: 'postscan',
    absender: 'Axa Versicherungen',
    betreff: 'Vertragsänderung Kollektiv-Kranken',
    kunde_name: 'Steiner Gastro AG',
    police_bezeichnung: 'KTG-2021-009',
    subject_type: 'firma',
    subject_id: 'ff000007-0000-0000-0000-000000000007',
    eingegangen_am: '2026-08-14T13:00:00.000Z',
    alter_tage: 12,
    stufe: 3,
    signale: [],
    verknuepft_mit: 'KTG-2021-009',
    fristenstufe_seit: '2026-08-14T13:00:00.000Z',
  },
  {
    id: 'kb000008-0000-0000-0000-000000000008',
    kanal: 'email',
    absender: 'Betreibungsamt Zürich',
    betreff: 'Zahlungsbefehl - Kenntnisnahme erforderlich',
    kunde_name: 'Delta Handels AG',
    police_bezeichnung: null,
    subject_type: 'firma',
    subject_id: 'ff000008-0000-0000-0000-000000000008',
    eingegangen_am: '2026-08-15T16:40:00.000Z',
    alter_tage: 11,
    stufe: 3,
    signale: [{ typ: 'priorisierter_absender', quelle: 'regelbasiert' }],
    verknuepft_mit: 'Delta Handels AG',
    fristenstufe_seit: '2026-08-15T16:40:00.000Z',
  },

  // ── Stufe 4 · Eskalation (≥14 Tage) ────────────────────────────────────────
  {
    id: 'kb000009-0000-0000-0000-000000000009',
    kanal: 'email',
    absender: 'Weber Immobilien AG',
    betreff: '3. Mahnung - Widerspruchsfrist läuft ab',
    kunde_name: 'Weber Immobilien AG',
    police_bezeichnung: null,
    subject_type: 'firma',
    subject_id: 'ff000009-0000-0000-0000-000000000009',
    eingegangen_am: '2026-08-10T08:00:00.000Z',
    alter_tage: 16,
    stufe: 4,
    signale: [
      { typ: 'wiederholter_kontakt', quelle: 'regelbasiert' },
      { typ: 'werthaltig', quelle: 'regelbasiert' },
    ],
    verknuepft_mit: 'Weber Immobilien AG',
    fristenstufe_seit: '2026-08-10T08:00:00.000Z',
  },
  {
    id: 'kb000010-0000-0000-0000-000000000010',
    kanal: 'email',
    absender: 'FINMA',
    betreff: 'Auskunftsersuchen GwG-Meldung',
    kunde_name: null,
    police_bezeichnung: null,
    subject_type: null,
    subject_id: null,
    eingegangen_am: '2026-08-11T09:30:00.000Z',
    alter_tage: 15,
    stufe: 4,
    signale: [{ typ: 'regulatorische_frist', quelle: 'regelbasiert' }],
    verknuepft_mit: null,
    fristenstufe_seit: '2026-08-11T09:30:00.000Z',
  },
];

/**
 * GET /admin/kontrolle/uebersicht — derived counts + highlight (St3/St4 non-empty).
 *
 * Stufe 3 carries a broker-CONFIGURED threshold (9, not the 10 default) so the fixture models a
 * tenant that tuned its Fristen — this is what lets the Kontrollliste header badge prove it reads
 * the configured `schwelle_tage` (same source as the dashboard tile), not the hard-coded default.
 */
const MOCK_UEBERSICHT_SCHWELLE: Record<1 | 2 | 3 | 4, number> = {
  1: STUFE_DEFAULT_TAGE[1],
  2: STUFE_DEFAULT_TAGE[2],
  3: 9,
  4: STUFE_DEFAULT_TAGE[4],
};
export const MOCK_UEBERSICHT: KontrolleUebersichtDto = {
  offen_total: MOCK_RUECKSTAND.length,
  highlight: true,
  stufen: ([1, 2, 3, 4] as const).map((stufe) => ({
    stufe,
    name: STUFE_NAME[stufe],
    schwelle_tage: MOCK_UEBERSICHT_SCHWELLE[stufe],
    count: MOCK_RUECKSTAND.filter((i) => i.stufe === stufe).length,
  })),
};

/** GET /admin/kontrolle/rueckstand?stufe=N — the per-stage list (oldest first). */
export function mockRueckstandForStufe(stufe: number): KontrolleRueckstandDetailDto[] {
  return MOCK_RUECKSTAND.filter((i) => i.stufe === stufe).sort((a, b) => b.alter_tage - a.alter_tage);
}

/** GET /admin/kontrolle/rueckstand/:id — one element. */
export function mockRueckstandDetail(id: string): KontrolleRueckstandDetailDto {
  return MOCK_RUECKSTAND.find((i) => i.id === id) ?? MOCK_RUECKSTAND[0]!;
}

/** GET /admin/einstellungen/kontrolle-fristen — the seeded defaults. */
export const MOCK_FRISTEN: KontrolleFristenDto = {
  stufen: ([1, 2, 3, 4] as const).map((stufe) => ({
    stufe,
    name: STUFE_NAME[stufe],
    tage_schwelle: STUFE_DEFAULT_TAGE[stufe],
    ist_default: true,
  })),
};
